/* eslint-disable react/forbid-prop-types,import/prefer-default-export,import/no-unresolved */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isIOS } from 'react-device-detect';
import {
  MAIN_ICON_COLORS,
  SelectCountryDropdown,
  PasswordValidationPopUp,
} from 'assets/components';
import { Tooltip } from 'react-tooltip';
import { OnboardingBodyLayout } from '../partials';
import Input from '../../../../components/Input';
import { ACCOUNT_REGIONS } from '../../../../utilities/constants';
import { US_COUNTRY_ALPHA } from '../../../../screens/Onboarding/constants';
import { handleUserEnvironmentUrl } from '../../../../utilities/helpers';
import Icon from '../../../../components/Icon';
import { ICONS } from '../../../../components/Icon/constants';
import { cacheUserEnvironment } from '../../../../utilities/caching';

export const SignupComponent = ({
  renderButtons,
  allowSeparateEnv,
  handleSubmitForm,
  form,
  handleChange,
  handleBlur,
  clearFormError,
  setShowPopUp,
  revealPassword,
  showPopUp,
  isEmailAllowed,
  isEmailLengthAllowed,
  pswdValidation,
  accountRegion,
  isAllowedCountry,
  featureAllowLimitAccessForAllowedCountries,
  isAllowedToSignup,
  waitingListWebsiteUrl,
  selectDropDownFields,
  renderFooter,
}) => {
  const { t: translate } = useTranslation();

  const renderWarningMessage = () => {
    if (!isAllowedCountry) {
      return (
        <a
          href={waitingListWebsiteUrl}
          target="_blank"
          className="alert alert-warning alert-action mt-4 letter-spacing-sm text-decoration-none cursor-pointer"
          style={{ marginBottom: '2.375rem' }}
          rel="noreferrer">
          <i className="icon icon-attention-other" />
          <span className="content px-1">
            {translate('not_available_in_your_country_note_message')}
          </span>
          <i className="icon icon-arrow-next-navigation" />
        </a>
      );
    }
    if (featureAllowLimitAccessForAllowedCountries && !isAllowedToSignup) {
      return (
        <a
          href={waitingListWebsiteUrl}
          target="_blank"
          className="alert alert-warning alert-action mt-4 letter-spacing-sm text-decoration-none cursor-pointer"
          style={{ marginBottom: '2.375rem' }}
          rel="noreferrer">
          <i className="icon icon-attention-other" />
          <span className="content px-1">{translate('service_note_available_yet')}</span>
          <i className="icon icon-arrow-next-navigation" />
        </a>
      );
    }
    return true;
  };

  const renderAccountRegionTabs = () => (
    <div className="d-flex flex-row align-items-center mt-4 w-100 gap-3 letter-spacing-sm flex-md-fill mb-2 mb-md-3">
      <div
        className={`d-flex flex-column align-items-center py-3 border rounded-extra w-50 border-2 ${
          accountRegion === ACCOUNT_REGIONS.UK_OR_EU
            ? 'bg-lighter border-primary-dimmed'
            : 'bg-light border-grey-lighter'
        }`}
        role="button"
        tabIndex="-1"
        onClick={() => {
          handleUserEnvironmentUrl('onboarding/signup', true);
        }}
        onKeyDown={() => {}}>
        <Icon icon={ICONS.INTERNATIONAL} color={MAIN_ICON_COLORS.WHITE} />
        <p
          className={`text-f6 mt-2 ${
            accountRegion === ACCOUNT_REGIONS.UK_OR_EU ? 'text-dark' : 'text-shadow'
          }`}>
          {translate('login_uk_eu_account_tab_title')}
        </p>
      </div>
      <div
        className={`d-flex flex-column align-items-center py-3 border rounded-extra w-50 border-2 ${
          accountRegion === ACCOUNT_REGIONS.US
            ? 'bg-lighter border-primary-dimmed'
            : 'bg-light border-grey-lighter'
        }`}
        role="button"
        tabIndex="-1"
        onClick={() => {
          cacheUserEnvironment(ACCOUNT_REGIONS.US);
          handleUserEnvironmentUrl('onboarding/signup');
        }}
        onKeyDown={() => {}}>
        <Icon icon={ICONS.USA} color={MAIN_ICON_COLORS.WHITE} />
        <p
          className={`text-f6 mt-2 ${
            accountRegion === ACCOUNT_REGIONS.US ? 'text-dark' : 'text-shadow'
          }`}>
          {translate('login_us_account_tab_title')}
        </p>
      </div>
    </div>
  );

  const getErrorLabel = () => {
    if (!isEmailLengthAllowed) {
      return translate('email_is_extra_long_error_label');
    }
    if (!isEmailAllowed) {
      return translate('email_not_supported_chars_error_label');
    }
    return translate('email_input_error_label');
  };

  return (
    <OnboardingBodyLayout renderButtons={renderButtons} title={translate('signup_title')}>
      {allowSeparateEnv && renderAccountRegionTabs()}
      <form onSubmit={handleSubmitForm} id="signup-form" className="letter-spacing-sm">
        <Input
          type="email"
          className={`${isIOS ? 'ios' : ''}`}
          groupClassName={` mt-4 pt-md-3 ${isIOS ? 'ios' : ''}`}
          placeholder={translate('label_email_address_input')}
          label={translate('label_email_address_input')}
          errorLabel={getErrorLabel()}
          name="email"
          value={form.email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          withFloatingLabel
          withErrorLabel
        />
        <Input
          type="password"
          className={`${isIOS ? 'ios' : ''}`}
          groupClassName={`mt-4 ${isIOS ? 'ios' : ''}`}
          placeholder={translate('password_input_label')}
          label={translate('password_input_label')}
          errorLabel={translate('password_input_error_label')}
          name="password"
          value={form.password}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={(e) => {
            clearFormError(e);
            setShowPopUp(true);
          }}
          required
          withFloatingLabel
          withErrorLabel
          inputGroupChildren={
            <div data-tooltip-id="password-validation-tooltip">
              <span
                className="icon icon-open-icon h4 text-shadow"
                role="button"
                onClick={revealPassword}
                onKeyDown={null}
                tabIndex={-1}
                aria-label="Reveal password"
              />
              <div className="d-md-none">
                <PasswordValidationPopUp
                  showPopUp={showPopUp}
                  translate={translate}
                  pswdValidation={pswdValidation}
                />
              </div>
            </div>
          }
        />
        <div className="select-group form-group my-0">
          <div className="w-100">
            <SelectCountryDropdown
              name="countryOfResidence"
              placeholder={translate('country_of_residence_input_label')}
              getOptionLabel={(option) => option.displayName}
              isOptionSelected={(option) => option.id === form.countryOfResidenceId}
              value={form.countryOfResidenceLabel}
              onChange={(data, name) => handleChange(data, name)}
              handleBlur={handleBlur}
              clearError={clearFormError}
              optionName="displayName"
              prefix=""
              containerClass="w-100 position-relative bg-light p-3 mt-2 rounded shadow-lg"
              fieldsInfo={selectDropDownFields}
              dropDownParentClass=""
              withValueImageFlag
              useFilteredCountries
              defaultValue={accountRegion === ACCOUNT_REGIONS.US ? US_COUNTRY_ALPHA : null}
              region={allowSeparateEnv ? accountRegion : null}
              readOnly={accountRegion === ACCOUNT_REGIONS.US}
            />
          </div>
        </div>
      </form>
      {renderFooter(renderWarningMessage)}
      <Tooltip
        id="password-validation-tooltip"
        isOpen={showPopUp}
        place="right"
        variant="dark"
        opacity={1}
        className="d-none d-md-block p-3 rounded border-1 password-validation-tooltip">
        <PasswordValidationPopUp showPopUp translate={translate} pswdValidation={pswdValidation} />
      </Tooltip>
    </OnboardingBodyLayout>
  );
};

SignupComponent.propTypes = {
  renderButtons: PropTypes.func.isRequired,
  allowSeparateEnv: PropTypes.bool.isRequired,
  handleSubmitForm: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  clearFormError: PropTypes.func.isRequired,
  setShowPopUp: PropTypes.func.isRequired,
  revealPassword: PropTypes.func.isRequired,
  showPopUp: PropTypes.bool.isRequired,
  pswdValidation: PropTypes.object.isRequired,
  accountRegion: PropTypes.string.isRequired,
  renderFooter: PropTypes.func.isRequired,
  featureAllowLimitAccessForAllowedCountries: PropTypes.bool.isRequired,
  isAllowedCountry: PropTypes.bool.isRequired,
  isEmailAllowed: PropTypes.bool.isRequired,
  isEmailLengthAllowed: PropTypes.bool.isRequired,
  isAllowedToSignup: PropTypes.bool.isRequired,
  waitingListWebsiteUrl: PropTypes.string.isRequired,
  selectDropDownFields: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
};
